import React, { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, MessageSquare } from 'lucide-react';
import { isSentryEnabled, showFeedbackDialog } from '@/lib/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  eventId?: string;
}

// Error translations for all supported languages
const errorTranslations = {
  de: {
    title: 'Etwas ist schiefgelaufen',
    subtitle: 'Unser Team wurde benachrichtigt',
    reportButton: 'Feedback senden',
    retryButton: 'Erneut versuchen',
  },
  en: {
    title: 'Something went wrong',
    subtitle: 'Our team has been notified',
    reportButton: 'Send feedback',
    retryButton: 'Try again',
  },
  tr: {
    title: 'Bir şeyler yanlış gitti',
    subtitle: 'Ekibimiz bilgilendirildi',
    reportButton: 'Geri bildirim gönder',
    retryButton: 'Tekrar dene',
  },
};

// Get user's preferred language
function getUserLanguage(): 'de' | 'en' | 'tr' {
  // Try to get from localStorage (where LanguageContext stores it)
  const stored = localStorage.getItem('ui-language');
  if (stored && (stored === 'de' || stored === 'en' || stored === 'tr')) {
    return stored;
  }
  
  // Fallback to browser language
  const browserLang = navigator.language.substring(0, 2);
  if (browserLang === 'de') return 'de';
  if (browserLang === 'tr') return 'tr';
  return 'en';
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Capture error with Sentry if enabled
    if (isSentryEnabled()) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          componentStack: errorInfo.componentStack,
        });
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, eventId: undefined });
  };

  handleFeedback = () => {
    showFeedbackDialog();
  };

  render() {
    if (this.state.hasError) {
      const lang = getUserLanguage();
      const t = errorTranslations[lang];
      
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-background text-foreground">
          <AlertTriangle className="h-16 w-16 text-destructive mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t.title}</h1>
          <p className="text-muted-foreground text-center mb-6 max-w-md">
            {t.subtitle}
          </p>
          <div className="flex gap-3">
            <Button onClick={this.handleRetry} variant="default">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t.retryButton}
            </Button>
            {isSentryEnabled() && (
              <Button onClick={this.handleFeedback} variant="outline">
                <MessageSquare className="h-4 w-4 mr-2" />
                {t.reportButton}
              </Button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
